/**
 * @fileoverview Authentication field validation (email / NPI / DEA / password / username)
 * @module services/auth
 * @description
 * Ported 1:1 from the Angular AuthValidationService. Exposed as a singleton
 * object (`authValidationService`) preserving the exact public method names.
 *
 * @author Liraz Amir
 */

export interface ValidationResult {
  isValid: boolean;
  message?: string;
}

export const authValidationService = {
  validateNPI(npi: string): ValidationResult {
    const cleaned = npi.replace(/\D/g, '');

    if (cleaned.length !== 10) {
      return { isValid: false, message: 'NPI must be exactly 10 digits' };
    }

    // Basic NPI validation - simplified checksum
    return { isValid: true };
  },

  validateDEA(dea: string): ValidationResult {
    const cleaned = dea.replace(/\s+/g, '').toUpperCase();

    if (cleaned.length < 9) {
      return { isValid: false, message: 'DEA number must be at least 9 characters' };
    }

    return { isValid: true };
  },

  validateEmail(email: string): ValidationResult {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email.trim()) {
      return { isValid: false, message: 'Email is required' };
    }

    if (!emailRegex.test(email)) {
      return { isValid: false, message: 'Please enter a valid email address' };
    }

    return { isValid: true };
  },

  validatePassword(password: string): ValidationResult {
    if (!password) {
      return { isValid: false, message: 'Password is required' };
    }

    if (password.length < 8) {
      return { isValid: false, message: 'Password must be at least 8 characters long' };
    }

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (!hasUpperCase) {
      return { isValid: false, message: 'Password must contain at least one uppercase letter' };
    }

    if (!hasLowerCase) {
      return { isValid: false, message: 'Password must contain at least one lowercase letter' };
    }

    if (!hasNumbers) {
      return { isValid: false, message: 'Password must contain at least one number' };
    }

    if (!hasSpecialChar) {
      return { isValid: false, message: 'Password must contain at least one special character' };
    }

    return { isValid: true };
  },

  /**
   * Generate a base username from user information
   * Format: [title.]lastname.firstInitial[.suffix]
   *
   * Examples:
   * - Dr. John Smith MD -> dr.smith.j.md
   * - Jane Doe -> doe.j
   * - Prof. Robert Van Der Berg PhD -> prof.van.der.berg.r.phd
   */
  generateUsername(firstName: string, lastName: string, title?: string, suffix?: string): string {
    if (!firstName || !lastName) return '';

    // Clean and prepare components
    const firstInitial = firstName.charAt(0).toLowerCase();

    // Handle multi-word last names (e.g., "Van Der Berg" -> "van.der.berg")
    const lastNameWords = lastName
      .split(/\s+/)
      .filter((word) => word.trim().length > 0)
      .map((word) => word.replace(/[^a-zA-Z0-9]/g, '').toLowerCase())
      .filter((word) => word.length > 0);

    const lastNameClean = lastNameWords.join('.');

    // Clean title and suffix if provided
    const titleClean = title ? title.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() : '';
    const suffixClean = suffix ? suffix.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() : '';

    // Build username with optional title and suffix
    const parts: string[] = [];
    if (titleClean) parts.push(titleClean);
    parts.push(lastNameClean);
    parts.push(firstInitial);
    if (suffixClean) parts.push(suffixClean);

    return parts.join('.');
  },
};

export type AuthValidationService = typeof authValidationService;
